// slidingDoorFrame.js
// Standalone SVG renderer for Lundia sliding-door frames.
// No dependencies, no DOM coupling — returns an SVG string.
//
// Usage (Shopify theme / plain <script>):
//   <script src="{{ 'slidingDoorFrame.js' | asset_url }}"></script>
//   <script>
//     container.innerHTML = SlidingDoorFrame.renderDoor({
//       count: 3, color: 'aluminum', panelWidthMm: 800, doorHeightMm: 2400
//     });
//   </script>
//
// Usage (bundler / ES module):
//   import 'slidingDoorFrame.js';        // side-effect import: registers window.SlidingDoorFrame
//   const { renderDoor } = window.SlidingDoorFrame;
//
// Usage (CommonJS):
//   const { renderDoor } = require('./slidingDoorFrame.js');

(function (global, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (global) {
    global.SlidingDoorFrame = api;
    // Register "omlijst" as the first door-type plugin. A door-type module is
    // { id, ranges, optionGroups:[..ids..], renderDoor(opts), heightMin, heightMax }.
    // omlijst's renderDoor delegates to the shared SVG renderer and ignores `vlak`.
    (global.CfgDoorTypes = global.CfgDoorTypes || {}).omlijst = {
      id: 'omlijst',
      ranges: { 2: [1180, 1980], 3: [1760, 2960], 4: [2360, 3960], 5: [2940, 4940] },
      heightMin: 1800,
      heightMax: 2850,
      optionGroups: ['paneelkleur', 'maten', 'aantal', 'profielkleur', 'spiegel', 'softclose', 'montage'],
      renderDoor: (opts) => api.renderDoor(opts),
    };
  }
}(typeof self !== 'undefined' ? self : typeof window !== 'undefined' ? window : null, function () {

const FRAME_MM_DEFAULTS = {
  topCapH: 55,
  topGapH: 1.5,
  topChannelH: 8,
  botTrackH: 15,
  sideFrameW: 16,
  mullionW: 5,
  sideFaceW: 13,
  sideInnerW: 2.2,
  hairlineW: 0.4,
};

const COLOR_PRESETS = {
  black:       { fill: '#1d1d1d', stroke: '#000',    depth: '#000',    useGradient: false },
  white:       { fill: '#f5f5f5', stroke: '#cfcfcf', depth: '#dadada', useGradient: false },
  aluminum:    { fill: 'GRAD',    stroke: '#7a7a7d', depth: '#5a5a5d', useGradient: true  },
  kwartsgrijs: { fill: '#8b877f', stroke: '#6c6862', depth: '#5b5751', useGradient: false },
  antraciet:   { fill: '#595d62', stroke: '#3f4247', depth: '#2f3236', useGradient: false },
};

const SHADOW_OPACITY = 0.32;

/**
 * Compute the total door width in mm given panel count, panel width, and side/mullion widths.
 */
function computeDoorWidthMm(count, panelWidthMm, frame) {
  return 2 * frame.sideFrameW + count * panelWidthMm + (count - 1) * frame.mullionW;
}

/**
 * Render a sliding-door frame as an SVG string.
 *
 * @param {Object}                            opts
 * @param {2|3|4|5}                           opts.count          panel count
 * @param {'black'|'white'|'aluminum'|Object} opts.color          frame color preset key or a custom color object matching COLOR_PRESETS shape
 * @param {number}                            opts.panelWidthMm   per-panel width in millimeters
 * @param {number}                            opts.doorHeightMm   door height in millimeters
 * @param {number}                            [opts.pxPerMm=0.15] render scale (sets svg width/height in px)
 * @param {Object}                            [opts.frame]        optional override of FRAME_MM_DEFAULTS
 * @param {string}                            [opts.idSuffix='']  unique suffix for SVG <defs> IDs (use when rendering multiple on one page)
 * @returns {string} SVG markup
 */
function renderDoor(opts) {
  const {
    count,
    color,
    panelWidthMm,
    doorHeightMm,
    pxPerMm = 0.15,
    frame = FRAME_MM_DEFAULTS,
    idSuffix = '',
  } = opts;

  const c = typeof color === 'string' ? COLOR_PRESETS[color] : color;
  if (!c) throw new Error(`renderDoor: unknown color "${color}"`);

  const F = { ...FRAME_MM_DEFAULTS, ...frame };
  const topAssemblyH = F.topCapH + F.topGapH + F.topChannelH;
  const doorWmm = computeDoorWidthMm(count, panelWidthMm, F);
  const innerH = doorHeightMm - topAssemblyH - F.botTrackH;
  const innerTopY = topAssemblyH;
  const innerBotY = doorHeightMm - F.botTrackH;
  const svgW = (doorWmm * pxPerMm).toFixed(1);
  const svgH = (doorHeightMm * pxPerMm).toFixed(1);

  let defs = '';
  if (c.useGradient) {
    defs += `<linearGradient id="alumGh_${idSuffix}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#aeaeb1"/>
      <stop offset="50%" stop-color="#cacacd"/>
      <stop offset="100%" stop-color="#a9a9ac"/>
    </linearGradient>
    <linearGradient id="alumGv_${idSuffix}" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#8d8d90"/>
      <stop offset="50%" stop-color="#dbdbdf"/>
      <stop offset="100%" stop-color="#8d8d90"/>
    </linearGradient>
    <linearGradient id="alumGi_${idSuffix}" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#6c6c6f"/>
      <stop offset="100%" stop-color="#9c9c9f"/>
    </linearGradient>`;
  }
  // Soft shadow gradients: a dark stop fading to transparent. Layered over the
  // panel fill, black-at-opacity darkens whatever colour is behind it — so the
  // shadow always reads as "a bit darker than the panel", on any decor.
  defs += `<linearGradient id="shL_${idSuffix}" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.42"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`
        + `<linearGradient id="shR_${idSuffix}" x1="1" y1="0" x2="0" y2="0"><stop offset="0%" stop-color="#000" stop-opacity="0.42"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`
        + `<linearGradient id="shT_${idSuffix}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#000" stop-opacity="0.38"/><stop offset="100%" stop-color="#000" stop-opacity="0"/></linearGradient>`;

  const fillH = c.useGradient ? `url(#alumGh_${idSuffix})` : c.fill;
  const fillV = c.useGradient ? `url(#alumGv_${idSuffix})` : c.fill;
  const fillInner = c.useGradient ? `url(#alumGi_${idSuffix})` : c.depth;
  const hair = '#000';

  let svg = `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${doorWmm} ${doorHeightMm}" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">`;
  svg += `<defs>${defs}</defs>`;

  // === TOP ASSEMBLY: cap (55mm) → gap (1.5mm) → channel (8mm) ===
  svg += `<rect x="0" y="0" width="${doorWmm}" height="${F.topCapH}" fill="${fillH}" stroke="${hair}" stroke-width="0.4"/>`;
  svg += `<rect x="0" y="${F.topCapH}" width="${doorWmm}" height="${F.topGapH}" fill="#000" opacity="0.55"/>`;
  svg += `<rect x="0" y="${F.topCapH + F.topGapH}" width="${doorWmm}" height="${F.topChannelH}" fill="${fillInner}" opacity="0.85" stroke="${hair}" stroke-width="0.3"/>`;

  // === BOTTOM TRACK ===
  svg += `<rect x="0" y="${innerBotY}" width="${doorWmm}" height="${F.botTrackH}" fill="${fillInner}" opacity="0.85" stroke="${hair}" stroke-width="0.3"/>`;
  svg += `<line x1="0" y1="${innerBotY}" x2="${doorWmm}" y2="${innerBotY}" stroke="${hair}" stroke-width="0.5"/>`;

  // === WANDPROFIEL (side, 16mm) — 4 strips: face / hairline / inner / hairline ===
  function sideProfile(xLeft, mirrored) {
    const { sideFaceW: fw, sideInnerW: iw, hairlineW: hw } = F;
    if (!mirrored) {
      svg += `<rect x="${xLeft}"               y="${innerTopY}" width="${fw}" height="${innerH}" fill="${fillV}"    stroke="${hair}" stroke-width="0.4"/>`;
      svg += `<rect x="${xLeft + fw}"          y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
      svg += `<rect x="${xLeft + fw + hw}"     y="${innerTopY}" width="${iw}" height="${innerH}" fill="${fillInner}" opacity="0.9"/>`;
      svg += `<rect x="${xLeft + fw + hw + iw}" y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
    } else {
      svg += `<rect x="${xLeft}"               y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
      svg += `<rect x="${xLeft + hw}"          y="${innerTopY}" width="${iw}" height="${innerH}" fill="${fillInner}" opacity="0.9"/>`;
      svg += `<rect x="${xLeft + hw + iw}"     y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
      svg += `<rect x="${xLeft + hw + iw + hw}" y="${innerTopY}" width="${fw}" height="${innerH}" fill="${fillV}"    stroke="${hair}" stroke-width="0.4"/>`;
    }
  }
  sideProfile(0, false);
  sideProfile(doorWmm - F.sideFrameW, true);

  // === MULLIONS (profiel, 5mm) — hairline / face / hairline ===
  const panelLefts = [];
  for (let i = 0; i < count; i++) {
    panelLefts.push(F.sideFrameW + i * panelWidthMm + i * F.mullionW);
  }
  for (let i = 0; i < count - 1; i++) {
    const mx = panelLefts[i] + panelWidthMm;
    const { mullionW: mw, hairlineW: hw } = F;
    svg += `<rect x="${mx}" y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
    const faceW = mw - 2 * hw;
    svg += `<rect x="${mx + hw}" y="${innerTopY}" width="${faceW}" height="${innerH}" fill="${fillV}" stroke="${hair}" stroke-width="0.3"/>`;
    svg += `<line x1="${mx + mw/2}" y1="${innerTopY}" x2="${mx + mw/2}" y2="${innerBotY}" stroke="${hair}" stroke-width="0.4" opacity="0.6"/>`;
    svg += `<rect x="${mx + mw - hw}" y="${innerTopY}" width="${hw}" height="${innerH}" fill="${hair}"/>`;
  }

  // === SHADOWS — soft gradients over the panel fill, so they read as a darker
  //     shade of the decor (a real recess shadow) rather than a flat grey line ===
  // top contact shadow under the channel
  svg += `<rect x="${F.sideFrameW}" y="${innerTopY}" width="${doorWmm - 2*F.sideFrameW}" height="14" fill="url(#shT_${idSuffix})"/>`;
  svg += `<rect x="${F.sideFrameW}" y="${innerBotY - 2}" width="${doorWmm - 2*F.sideFrameW}" height="2" fill="#000" opacity="${SHADOW_OPACITY * 0.4}"/>`;

  // Front-vs-back overlap: soft shadow on the recessed (back) panel near the seam
  const shadowBand = Math.min(18, panelWidthMm * 0.22);
  for (let i = 0; i < count; i++) {
    if (i % 2 === 0) continue;            // back (recessed) panels = odd indices
    const px = panelLefts[i];
    svg += `<rect x="${px}" y="${innerTopY}" width="${shadowBand}" height="${innerH}" fill="url(#shL_${idSuffix})"/>`;
    if (i < count - 1) {
      svg += `<rect x="${px + panelWidthMm - shadowBand}" y="${innerTopY}" width="${shadowBand}" height="${innerH}" fill="url(#shR_${idSuffix})"/>`;
    }
  }

  svg += `</svg>`;
  return svg;
}

/**
 * Render a complete door SVG (panel fills behind the frame) — used for the cart
 * thumbnail. Fills: mirror gradient, material image (cover, bottom-anchored), or
 * solid hex. The frame is nested on top.
 *
 * @param {Object} opts
 * @param {2|3|4|5} opts.count
 * @param {number}  opts.totalWidthMm   total opening width (panel width is derived)
 * @param {number}  opts.doorHeightMm
 * @param {string}  [opts.color='black'] frame colour key
 * @param {string}  [opts.hex]           solid panel colour (when no material)
 * @param {string}  [opts.material]      material image URL
 * @param {number[]}[opts.mirrors=[]]    door indices that are mirrored
 * @param {string}  [opts.idSuffix='c']
 * @returns {string} SVG markup
 */
function renderDoorComposite(opts) {
  const {
    count, totalWidthMm, doorHeightMm,
    color = 'black', hex = '#cccccc', material = '', mirrors = [], idSuffix = 'c',
  } = opts;
  const F = FRAME_MM_DEFAULTS;
  const panelWidthMm = (totalWidthMm - 2 * F.sideFrameW - (count - 1) * F.mullionW) / count;
  const doorWmm = computeDoorWidthMm(count, panelWidthMm, F);
  const innerTopY = F.topCapH + F.topGapH + F.topChannelH;
  const innerH = doorHeightMm - innerTopY - F.botTrackH;

  let fills = '', defs = '', hasMirror = false;
  for (let i = 0; i < count; i++) {
    const x = F.sideFrameW + i * (panelWidthMm + F.mullionW);
    if (mirrors.indexOf(i) !== -1) {
      hasMirror = true;
      fills += `<rect x="${x}" y="${innerTopY}" width="${panelWidthMm}" height="${innerH}" fill="url(#cfgMir_${idSuffix})"/>`;
    } else if (material) {
      const href = String(material).replace(/&/g, '&amp;');
      fills += `<image href="${href}" x="${x}" y="${innerTopY}" width="${panelWidthMm}" height="${innerH}" preserveAspectRatio="xMidYMax slice"/>`;
    } else {
      fills += `<rect x="${x}" y="${innerTopY}" width="${panelWidthMm}" height="${innerH}" fill="${hex}"/>`;
    }
  }
  if (hasMirror) {
    defs = `<linearGradient id="cfgMir_${idSuffix}" x1="0" y1="0" x2="1" y2="1">` +
      `<stop offset="0%" stop-color="#cdd5da"/><stop offset="20%" stop-color="#eef2f4"/>` +
      `<stop offset="45%" stop-color="#aeb8be"/><stop offset="65%" stop-color="#f4f7f8"/>` +
      `<stop offset="85%" stop-color="#b7c0c6"/><stop offset="100%" stop-color="#dce2e5"/></linearGradient>`;
  }
  const frame = renderDoor({ count, color, panelWidthMm, doorHeightMm, pxPerMm: 1, idSuffix });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${doorWmm} ${doorHeightMm}" preserveAspectRatio="xMidYMid meet"><defs>${defs}</defs>${fills}${frame}</svg>`;
}

return { renderDoor, renderDoorComposite, computeDoorWidthMm, FRAME_MM_DEFAULTS, COLOR_PRESETS };

}));
